import { useEffect, useState } from "react";
import { Plus, Crown, Shield, Users as UsersIcon, Settings, GripVertical, LogIn } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CreateGroupDialog } from "./CreateGroupDialog";
import GroupSettingsDialog from "./GroupSettingsDialog";
import { cn } from "@/lib/utils";
import { usePermissionRedirect } from "@/hooks/usePermissionRedirect";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getCurrentUser } from "@/lib/authUser";

interface Group {
  id: string;
  name: string;
  role?: string;
  member_count?: number;
}

interface SortableGroupItemProps {
  group: Group;
  isSelected: boolean;
  onSelect: (groupId: string) => void;
  onOpenSettings: (group: Group, e: React.MouseEvent) => void;
}

function SortableGroupItem({ group, isSelected, onSelect, onOpenSettings }: SortableGroupItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <button
        onClick={() => onSelect(group.id)}
        className={cn(
          "w-full text-left p-3 rounded-lg border transition-all hover:bg-secondary/80",
          isSelected
            ? "bg-secondary border-primary"
            : "bg-background border-border"
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <div 
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing flex-shrink-0 p-1 hover:bg-muted rounded"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm truncate">{group.name}</span>
              {group.role === "leader" && (
                <Crown className="h-3 w-3 text-yellow-500 flex-shrink-0" />
              )}
              {group.role === "admin" && (
                <Shield className="h-3 w-3 text-blue-500 flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
              <UsersIcon className="h-3 w-3 flex-shrink-0" />
              <span>{group.member_count}</span>
              <span>•</span>
              <span className="capitalize">{group.role}</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={(e) => onOpenSettings(group, e)}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </button>
    </div>
  );
}

export function GroupsList() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [selectedSettingsGroup, setSelectedSettingsGroup] = useState<Group | null>(null);
  const [inviteInput, setInviteInput] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const { selectedGroupId, setSelectedGroupId } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkGroupLimit } = usePermissionRedirect();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchGroups();

    // Subscribe to real-time updates for groups
    const channel = supabase
      .channel('groups-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'groups'
        },
        () => {
          fetchGroups();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchGroups = async () => {
    // Fetch groups with member info
    const { data, error } = await supabase
      .from("groups")
      .select(`
        id, 
        name,
        group_members!inner(role, user_id)
      `)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching groups:", error);
      return;
    }

    // Transform data to get user's role in each group
    const user = await getCurrentUser();
    if (!user) return;

    const groupsWithRoles = data?.map((group: any) => {
      const userMember = group.group_members.find((m: any) => m.user_id === user.id);
      return {
        id: group.id,
        name: group.name,
        role: userMember?.role || "member",
        member_count: group.group_members.length,
      };
    }) || [];

    // Load custom order from profile
    const { data: profileData } = await supabase
      .from("profiles")
      .select("group_order")
      .eq("user_id", user.id)
      .single();

    const customOrder = (profileData?.group_order as string[]) || [];
    
    // Sort groups based on custom order
    const sortedGroups = [...groupsWithRoles].sort((a, b) => {
      const indexA = customOrder.indexOf(a.id);
      const indexB = customOrder.indexOf(b.id);
      
      // If both have custom positions, sort by those
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A has a custom position, it comes first
      if (indexA !== -1) return -1;
      // If only B has a custom position, it comes first
      if (indexB !== -1) return 1;
      // Neither has custom position, maintain original order
      return 0;
    });

    setGroups(sortedGroups);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = groups.findIndex((g) => g.id === active.id);
      const newIndex = groups.findIndex((g) => g.id === over.id);

      const newGroups = arrayMove(groups, oldIndex, newIndex);
      setGroups(newGroups);

      // Save new order to profile
      const user = await getCurrentUser();
      if (user) {
        const groupOrder = newGroups.map(g => g.id);
        await supabase
          .from("profiles")
          .update({ group_order: groupOrder })
          .eq("user_id", user.id);
      }
    }
  };

  const handleSelectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
    
    // Extract the current page (hot, cold, refresher, setup)
    const currentPath = location.pathname.split('/').pop() || 'hot';
    
    // Navigate to the new group URL
    navigate(`/dashboard/groups/${groupId}/${currentPath}`);
  };

  const handleOpenSettings = (group: Group, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent group selection
    setSelectedSettingsGroup(group);
    setSettingsDialogOpen(true);
  };

  const handleJoinGroup = async () => {
    if (!inviteInput.trim()) return;
    setJoinLoading(true);

    try {
      // Extract code from full URL or raw code
      let code = inviteInput.trim();
      const parts = code.split("/").filter(Boolean);
      if (parts.length > 1) {
        code = parts[parts.length - 1];
      }

      // Validate invite
      const { data: validation, error: valErr } = await supabase
        .rpc("validate_invite_code", { invite_code_input: code });
      const result = Array.isArray(validation) ? validation[0] : validation;
      if (valErr || !result?.is_valid) {
        toast.error("Invalid or expired invite link");
        return;
      }

      // Check group limit
      if (!(await checkGroupLimit())) return;

      // Join group
      const { data, error: joinErr } = await supabase.rpc("join_group_with_invite", {
        invite_code_param: code,
      });
      if (joinErr) {
        if (joinErr.message.includes("Already a member")) {
          toast.success("You're already a member of this group");
        } else {
          throw joinErr;
        }
      } else {
        toast.success("Joined group successfully");
      }

      const groupId = result?.group_id || (data as any)?.group_id;
      if (groupId) {
        await fetchGroups();
        setSelectedGroupId(groupId);
        const currentPath = location.pathname.split("/").pop() || "hot";
        navigate(`/dashboard/groups/${groupId}/${currentPath}`);
        setInviteInput("");
      }
    } catch (e: any) {
      toast.error("Failed to join group", { description: e.message });
    } finally {
      setJoinLoading(false);
    }
  };

  return (
    <div className="space-y-2 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-muted-foreground">Your Groups</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCreateDialogOpen(true)}
          className="gap-1 h-8"
        >
          <Plus className="h-3 w-3" />
          New
        </Button>
      </div>

      <div className="space-y-1">
        {groups.length === 0 ? (
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-2">No groups yet</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Your First Group
            </Button>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={groups.map(g => g.id)}
              strategy={verticalListSortingStrategy}
            >
              {groups.map((group) => (
                <SortableGroupItem
                  key={group.id}
                  group={group}
                  isSelected={selectedGroupId === group.id}
                  onSelect={handleSelectGroup}
                  onOpenSettings={handleOpenSettings}
                />
              ))}
            </SortableContext>
        </DndContext>
      )}
      </div>

      <div className="pt-2 border-t border-border">
        <h4 className="text-xs font-medium text-muted-foreground mb-2">Join with invite</h4>
        <div className="flex gap-2">
          <Input
            placeholder="Paste invite link or code"
            value={inviteInput}
            onChange={(e) => setInviteInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleJoinGroup()}
            disabled={joinLoading}
            className="h-9 text-sm"
          />
          <Button
            size="sm"
            className="h-9 px-3 shrink-0"
            onClick={handleJoinGroup}
            disabled={joinLoading || !inviteInput.trim()}
          >
            <LogIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <CreateGroupDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onGroupCreated={(groupId) => {
          fetchGroups();
          setSelectedGroupId(groupId);
          // Navigate to the new group
          const currentPath = location.pathname.split('/').pop() || 'hot';
          navigate(`/dashboard/groups/${groupId}/${currentPath}`);
        }}
      />

      {selectedSettingsGroup && (
        <GroupSettingsDialog
          open={settingsDialogOpen}
          onOpenChange={setSettingsDialogOpen}
          groupId={selectedSettingsGroup.id}
          groupName={selectedSettingsGroup.name}
          onGroupUpdated={fetchGroups}
        />
      )}
    </div>
  );
}
